import React, { useEffect, useMemo, useState } from "react";
import SectionCard from "../ui/SectionCard";
import Button from "../../../../components/Button/Button";
import {
  getExpenseClaimById,
  submitExpenseClaim,
} from "../../../../services/expenseServices";
import { FaClipboardCheck, FaSyncAlt, FaPaperPlane } from "react-icons/fa";

const money = (v) => Number(v || 0).toFixed(2);

const badge = (statut) => {
  const s = String(statut || "draft");
  if (s === "draft") return "bg-slate-100 text-slate-800 ring-slate-200";
  if (s === "submitted") return "bg-sky-100 text-sky-800 ring-sky-200";
  if (s === "approved")
    return "bg-emerald-100 text-emerald-800 ring-emerald-200";
  if (s === "rejected") return "bg-rose-100 text-rose-800 ring-rose-200";
  if (s === "paid") return "bg-violet-100 text-violet-800 ring-violet-200";
  return "bg-slate-100 text-slate-800 ring-slate-200";
};

const num = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const pickItemsArray = (claim) => {
  if (!claim) return [];
  // selon ton include Sequelize
  if (Array.isArray(claim.ClaimItems)) return claim.ClaimItems;
  // fallback si un jour tu changes de shape
  if (Array.isArray(claim.items)) return claim.items;
  if (Array.isArray(claim.expense_items)) return claim.expense_items;
  return [];
};

const getIkCoef = (claim) => {
  if (claim?.moyen_transport !== "voiture") return 0;

  const cov = !!claim?.covoiturage;
  if (!cov) return 0.28;

  // covoiturage actif
  const role = claim?.covoiturage_role;
  if (role === "Passager") return 0; // pas d'IK pour le passager

  const n = Math.max(
    0,
    Math.floor(Number(claim?.covoiturage_nb_passagers ?? 0)),
  );

  if (n === 0) return 0.28; // conducteur + "covoiturage" mais 0 passager -> même coef
  if (n === 1) return 0.38;
  if (n === 2) return 0.48;
  return 0.58; // 3+
};

export default function ClaimSummaryCard({
  claimId,
  onEnsureClaim,
  onStatusChange,
}) {
  const [claim, setClaim] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const refresh = async () => {
    if (!claimId) return;
    try {
      setError("");
      setLoading(true);
      const data = await getExpenseClaimById(claimId);
      setClaim(data || null);
    } catch (e) {
      console.error(e);
      setError("Impossible de charger le récapitulatif de la fiche.");
      setClaim(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimId]);

  const canSubmit = useMemo(() => {
    return !!claimId && claim?.statut === "draft";
  }, [claimId, claim?.statut]);

  // ✅ Calcul front des totaux à partir des lignes
  const computed = useMemo(() => {
    const items = pickItemsArray(claim);
    const sumItems = items.reduce((acc, it) => acc + num(it?.montant), 0);

    const trajet = num(claim?.montant_trajet);
    const indTenue = num(claim?.indemnite_tenue);
    const indRep = num(claim?.indemnite_representation);

    // Si le back n’envoie pas/plus à jour : fallback sur calcul front
    const serverTotalFrais = num(claim?.total_frais);
    const totalFrais =
      serverTotalFrais > 0 || sumItems === 0 ? serverTotalFrais : sumItems;

    const serverTotalGeneral = num(claim?.total_general);
    const totalGeneralComputed = totalFrais + trajet + indTenue + indRep;
    const totalGeneral =
      serverTotalGeneral > 0 || totalGeneralComputed === 0
        ? serverTotalGeneral
        : totalGeneralComputed;

    const km = num(claim?.distance_km);
    const kmTotal = claim?.aller_retour ? km * 2 : km;

    const coefIk = getIkCoef(claim);
    const ikCalc = kmTotal * coefIk;

    // valeur "serveur" si elle existe (souvent c’est celle à payer)
    const ikServer = num(claim?.montant_trajet);

    return {
      sumItems,
      totalFrais,
      totalGeneral,
      trajet,
      indTenue,
      indRep,
      km,
      kmTotal,
      coefIk,
      ikCalc,
      ikServer,
    };
  }, [claim]);

  const doSubmit = async () => {
    if (!claimId) return;
    if (
      !window.confirm(
        "Soumettre la fiche ? Après soumission, la modification peut être bloquée.",
      )
    )
      return;

    try {
      setError("");
      setSubmitting(true);
      await submitExpenseClaim(claimId);
      await refresh();
      onStatusChange?.("submitted");
    } catch (e) {
      console.error(e);
      setError("Erreur lors de la soumission.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SectionCard
      id="recap"
      title="Récapitulatif"
      subtitle="Vérifiez les totaux puis soumettez votre fiche. Appuyez sur Rafraîchir après toute modification pour mettre à jour ce récapitulatif."
      icon={<FaClipboardCheck />}
      actions={
        <div className="flex flex-col gap-2">
          <Button
            text={loading ? "Chargement..." : "Rafraîchir"}
            variant="outline"
            size="sm"
            onClick={refresh}
            icon={FaSyncAlt}
            disabled={!claimId}
          />
          <Button
            text={submitting ? "Soumission..." : "Soumettre"}
            variant="primary"
            size="sm"
            onClick={doSubmit}
            icon={FaPaperPlane}
            disabled={!canSubmit || submitting}
          />
        </div>
      }
    >
      {!claimId ? (
        <div className="rounded-xl p-4 bg-amber-50 text-amber-900 ring-1 ring-amber-200">
          Enregistre d’abord la fiche pour obtenir un <strong>id</strong>.
          <div className="mt-3">
            <button
              type="button"
              className="h-10 px-4 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700"
              onClick={onEnsureClaim}
            >
              Enregistrer maintenant
            </button>
          </div>
        </div>
      ) : null}

      {error ? (
        <div className="mt-3 rounded-xl p-3 bg-rose-50 text-rose-800 ring-1 ring-rose-200">
          {error}
        </div>
      ) : null}

      {claimId ? (
        <div className="mt-4 rounded-2xl ring-1 ring-slate-200 bg-white p-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="font-semibold text-slate-800">Fiche #{claimId}</div>
            <span
              className={[
                "inline-flex items-center px-2.5 py-1 rounded-full text-xs ring-1",
                badge(claim?.statut),
              ].join(" ")}
            >
              {claim?.statut || "draft"}
            </span>
          </div>

          <div className="mt-3 grid gap-3 md:grid-cols-2">
            <div className="rounded-xl ring-1 ring-slate-200 bg-slate-50 p-3">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                Total frais (lignes)
              </div>
              <div className="mt-1 text-lg font-semibold">
                {money(computed.totalFrais)} €
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {pickItemsArray(claim).length} ligne(s)
              </div>
            </div>

            <div className="rounded-xl ring-1 ring-slate-200 bg-slate-50 p-3">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                Indemnités
              </div>
              <div className="mt-1 text-sm text-slate-700">
                Tenue : <strong>{money(computed.indTenue)} €</strong>
              </div>
              <div className="text-sm text-slate-700">
                Représentation : <strong>{money(computed.indRep)} €</strong>
              </div>
            </div>

            <div className="rounded-xl ring-1 ring-slate-200 bg-slate-50 p-3">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                Indemnités kilométriques
              </div>

              <div className="mt-1 text-lg font-semibold">
                {money(computed.ikCalc)} €
              </div>

              <div className="text-xs text-slate-600 mt-2">
                Distance : <strong>{computed.kmTotal}</strong> km{" "}
                {claim?.aller_retour ? (
                  <span>(A/R)</span>
                ) : (
                  <span>(aller simple)</span>
                )}{" "}
                • Coef : <strong>{computed.coefIk.toFixed(2)} €/km</strong>
              </div>

              {/* Optionnel : montrer ce que renvoie le back si différent */}
              {num(claim?.montant_trajet) > 0 &&
              Math.abs(num(claim?.montant_trajet) - computed.ikCalc) > 0.01 ? (
                <div className="text-xs text-amber-700 mt-2">
                  Montant enregistré (serveur) :{" "}
                  <strong>{money(claim?.montant_trajet)} €</strong> — différence
                  :{" "}
                  <strong>
                    {money(num(claim?.montant_trajet) - computed.ikCalc)} €
                  </strong>
                </div>
              ) : null}
            </div>

            <div className="rounded-xl ring-1 ring-slate-200 bg-emerald-50 p-3">
              <div className="text-xs uppercase tracking-wide text-emerald-700">
                Total général
              </div>
              <div className="mt-1 text-2xl font-extrabold text-emerald-900">
                {money(computed.totalGeneral)} €
              </div>
            </div>
          </div>

          {claim?.statut !== "draft" ? (
            <div className="mt-3 rounded-xl p-3 bg-sky-50 text-sky-800 ring-1 ring-sky-200 text-sm">
              Cette fiche n’est plus en brouillon : certaines modifications
              peuvent être bloquées côté serveur.
            </div>
          ) : null}
        </div>
      ) : null}
    </SectionCard>
  );
}
